import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, Divider,
    IconButton, Alert, CircularProgress, MenuItem,
    Snackbar, FormControlLabel, Switch, Checkbox, ListItemText,
    OutlinedInput, Chip, FormControl, InputLabel, Select
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import api from '../services/api';
import { isVisualizador } from '../services/auth';

const FORMAS = [
    { value: 'PIX', label: 'PIX' },
    { value: 'DINHEIRO', label: 'Dinheiro' },
    { value: 'CARTAO_CREDITO', label: 'Cartão de Crédito' },
    { value: 'CARTAO_DEBITO', label: 'Cartão de Débito' },
    { value: 'BOLETO', label: 'Boleto' },
    { value: 'TRANSFERENCIA', label: 'Transferência' },
];

const initialForm = {
    nome: '', descricao: '', formasPagamento: [],
    idContaBancaria: '', ativo: true
};

export default function PlanoPagamentoForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const soLeitura = isVisualizador();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(!!id);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [contas, setContas] = useState([]);
    const [formData, setFormData] = useState(initialForm);

    useEffect(() => {
        api.get('/contas-bancarias').then(r => {
            const d = r.data;
            const list = Array.isArray(d) ? d : (d?.content ?? []);
            setContas(list.filter(c => c.ativo));
        }).catch(() => {});

        if (id) {
            api.get(`/planos-pagamento/${id}`)
                .then(r => {
                    const d = r.data;
                    setFormData({
                        nome: d.nome || '',
                        descricao: d.descricao || '',
                        formasPagamento: Array.isArray(d.formasPagamento) ? d.formasPagamento : [],
                        idContaBancaria: d.idContaBancaria || '',
                        ativo: d.ativo ?? true,
                    });
                })
                .catch(() => setError('Não foi possível carregar os dados.'))
                .finally(() => setFetching(false));
        }
    }, [id]);

    const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

    const handleSave = async () => {
        if (!formData.nome.trim()) { setError('O nome é obrigatório.'); return; }
        if (!formData.formasPagamento || formData.formasPagamento.length === 0) { setError('Selecione ao menos uma forma de pagamento.'); return; }
        if (!formData.idContaBancaria) { setError('Vincule uma conta bancária.'); return; }

        setError('');
        setLoading(true);
        try {
            const payload = {
                ...formData,
                idContaBancaria: formData.idContaBancaria || null,
            };
            if (id) {
                await api.put(`/planos-pagamento/${id}`, payload);
            } else {
                await api.post('/planos-pagamento', payload);
            }
            setSnackbar({ open: true, message: id ? 'Plano atualizado com sucesso!' : 'Plano criado com sucesso!', severity: 'success' });
            setTimeout(() => navigate('/plano-pagamento'), 1500);
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao salvar.');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/plano-pagamento')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {soLeitura ? 'Visualizar Plano de Pagamento' : id ? 'Editar Plano de Pagamento' : 'Novo Plano de Pagamento'}
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                    Dados do Plano
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField fullWidth label="Nome *" value={formData.nome} onChange={set('nome')} disabled={soLeitura} placeholder="Ex: PIX Nubank, Cartão Itaú" />

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <FormControl sx={{ flex: 1, minWidth: 240 }} disabled={soLeitura}>
                            <InputLabel id="formas-pagamento-label">Formas de Pagamento *</InputLabel>
                            <Select
                                labelId="formas-pagamento-label"
                                multiple
                                sx={{
                                    borderRadius: '8px',
                                    minHeight: 56,
                                    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', minHeight: '23px !important', py: '16.5px' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderWidth: 1 },
                                }}
                                value={formData.formasPagamento}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setFormData(prev => ({ ...prev, formasPagamento: typeof v === 'string' ? v.split(',') : v }));
                                }}
                                input={<OutlinedInput label="Formas de Pagamento *" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map(v => (
                                            <Chip key={v} size="small" label={FORMAS.find(f => f.value === v)?.label || v} />
                                        ))}
                                    </Box>
                                )}
                            >
                                {FORMAS.map(f => (
                                    <MenuItem key={f.value} value={f.value}>
                                        <Checkbox checked={formData.formasPagamento.indexOf(f.value) > -1} />
                                        <ListItemText primary={f.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            select
                            sx={{ flex: 1, minWidth: 240 }}
                            label="Conta Bancária *"
                            value={formData.idContaBancaria}
                            onChange={set('idContaBancaria')}
                            disabled={soLeitura}
                            SelectProps={{
                                renderValue: (selected) => {
                                    if (!selected) return <em style={{ color: '#999' }}>Selecione</em>;
                                    const c = contas.find(x => x.id_c_bancaria === selected);
                                    return c ? `${c.banco} — Ag: ${c.agencia} / ${c.numeroConta}` : selected;
                                },
                                MenuProps: { PaperProps: { sx: { maxHeight: 360 } } }
                            }}
                        >
                            <MenuItem value=""><em>Selecione</em></MenuItem>
                            {contas.map(c => (
                                <MenuItem key={c.id_c_bancaria} value={c.id_c_bancaria} sx={{ alignItems: 'flex-start', py: 1 }}>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccountBalanceIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.banco}</Typography>
                                            </Box>
                                        }
                                        secondary={`Ag ${c.agencia} • Cc ${c.numeroConta}`}
                                    />
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <TextField fullWidth label="Descrição" value={formData.descricao} onChange={set('descricao')} multiline rows={2} disabled={soLeitura} />
                </Box>

                <Divider sx={{ my: 4 }} />

                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.ativo}
                            onChange={(e) => setFormData(prev => ({ ...prev, ativo: e.target.checked }))}
                            color="success"
                            disabled={soLeitura}
                        />
                    }
                    label="Plano ativo"
                />

                <Divider sx={{ my: 4 }} />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={() => navigate('/plano-pagamento')} sx={{ color: '#666', borderColor: '#ccc' }}>
                        {soLeitura ? 'Voltar' : 'Cancelar'}
                    </Button>
                    {!soLeitura && (
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            sx={{ px: 5 }}
                        >
                            {id ? 'Salvar Alterações' : 'Criar Plano'}
                        </Button>
                    )}
                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
